import { mainAPIUrl } from '../../../settings';
import { isDevelopment } from '../env/config';

const baseURL = `${mainAPIUrl}/gservices_3/ss`;

/**
 * Update isSkuRevealedByRecipient for SmartSend order
 * Endpoint: POST /gservices_3/ss/orders/:ssid/reveal_gesture
 *
 * @param {string} ssid - SmartSend ID
 * @param {boolean} isRevealed - true if recipient revealed SKU, false if kept as surprise
 * @returns {Promise<{success: boolean, error?: string, code?: string, data?: any}>}
 *
 * Note: No nonce required - this is a one-time action (frontend doesn't show step again),
 * has no external API costs, and ssid is unguessable (UUID v4)
 */
export const updateSkuRevealStatus = async (ssid, isRevealed) => {
  const url = `${baseURL}/orders/${ssid}/reveal_gesture`;

  // Validate isRevealed parameter
  if (typeof isRevealed !== 'boolean') {
    return {
      success: false,
      error: 'isRevealed must be a boolean value.',
      code: 'INVALID_PARAMETER',
    };
  }

  // Add timeout to prevent hanging requests
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 second timeout

  try {
    const headers = {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    };

    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify({ isSkuRevealedByRecipient: isRevealed }),
      signal: controller.signal,
    });

    // Check if response is ok before trying to parse JSON
    if (!response.ok) {
      // Try to parse error response, but handle cases where response isn't JSON
      let errorData = {};
      try {
        errorData = await response.json();
      } catch (parseError) {
        // Response isn't JSON, use status text
        return {
          success: false,
          error: `Server error: ${response.status} ${response.statusText}`,
          code: 'HTTP_ERROR',
        };
      }

      return {
        success: false,
        error: errorData.message || errorData.error || 'Failed to update SKU reveal status',
        code: errorData.code || 'ERROR',
        data: errorData,
      };
    }

    // Parse successful response
    const json = await response.json();

    // Success - return success status
    return {
      success: true,
      code: json.code,
    };
  } catch (error) {
    // Handle timeout errors
    if (error.name === 'AbortError') {
      console.error('Update SKU reveal status timeout:', error);
      return {
        success: false,
        error: 'Request timeout. Please try again.',
      };
    }

    // Handle network errors, CORS, etc.
    if (error.name === 'TypeError') {
      const errorMessage = error.message?.toLowerCase() || '';
      const isNetworkError =
        errorMessage.includes('fetch') ||
        errorMessage.includes('network') ||
        errorMessage.includes('failed to') ||
        errorMessage.includes('cors') ||
        errorMessage.includes('load failed');

      if (isNetworkError) {
        if (isDevelopment()) {
          console.error('Update SKU reveal status network error (dev details):', {
            error,
            baseURL,
            url,
          });
        } else {
          console.error('Update SKU reveal status network error:', error);
        }
        return {
          success: false,
          error: 'Network error: Unable to reach server. Please check your internet connection and try again.',
        };
      }
    }

    console.error('Update SKU reveal status error:', error);
    return {
      success: false,
      error: error.message || 'An error occurred. Please try again.',
    };
  } finally {
    // Always clear timeout
    clearTimeout(timeoutId);
  }
};

/**
 * Mark SKU as kept a surprise for SmartSend order
 * Convenience wrapper for updateSkuRevealStatus with isRevealed = false
 *
 * @param {string} ssid - SmartSend ID
 * @returns {Promise<{success: boolean, error?: string, code?: string, data?: any}>}
 */
export const keepSkuSurprise = async (ssid) => {
  return updateSkuRevealStatus(ssid, false);
};
